import type { Workspace } from '@stratumn/studio-shared';
import { motion } from 'framer-motion';
import { Pencil, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { WorkspaceFormValues } from '../form/form.workspace';
import { WorkspaceForm } from '../form/form.workspace';
import { VersionBadge } from './VersionBadge';

const getEditDefaults = (ws: Workspace): WorkspaceFormValues => ({
  name: ws.name,
  color: ws.color || '#6366f1',
  version: ws.version ?? null
});

export type GeneralSectionProps = {
  workspace: Workspace;
  processId: string;
  isEditing: boolean;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSubmit: (values: WorkspaceFormValues) => Promise<void>;
};

export const GeneralSection = ({
  workspace,
  processId,
  isEditing,
  onStartEditing,
  onCancelEditing,
  onSubmit
}: GeneralSectionProps) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-balance font-semibold text-lg">General</h2>
        {!isEditing && (
          <div className="flex items-center gap-2">
            <Button onClick={onStartEditing} size="sm" variant="outline">
              <Pencil />
              Edit
            </Button>
            <Button
              onClick={() =>
                navigate(
                  `/${processId}/generation/new?workspaceId=${workspace.id}`
                )
              }
              size="sm"
              variant="default"
            >
              <Sparkles className="size-4" />
              Generate
            </Button>
          </div>
        )}
      </div>

      <motion.div
        animate={{ height: 'auto' }}
        className="overflow-hidden rounded-xl bg-card p-6 shadow-verail"
        initial={false}
        transition={{ type: 'spring', duration: 0.35, bounce: 0 }}
      >
        {isEditing ? (
          <WorkspaceForm
            defaultValues={getEditDefaults(workspace)}
            onCancel={onCancelEditing}
            onSubmit={onSubmit}
            processId={processId}
            submitLabel="Save"
          />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 w-4 shrink-0 rounded-full"
                style={{
                  backgroundColor: workspace.color || '#6366f1'
                }}
              />
              <span className="font-medium">{workspace.name}</span>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-muted-foreground text-xs uppercase tracking-wide">
                Version
              </span>
              <div className="flex items-center gap-2.5">
                <VersionBadge version={workspace.version} />
                {workspace.version && (
                  <span className="font-mono text-muted-foreground text-xs">
                    {workspace.version.name}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};
